import { isRecordType } from "@karrotmarket-com/type-utility";
import type { Maybe } from "__generated__/jampot/alpha-jp";
import { isString } from "lodash";
import { P, match } from "ts-pattern";
import z from "zod";

import { truncateMetaDescription } from "./truncateMetaDescription";

export type JpTitleData = {
  region: {
    name: string;
    depth1RegionName?: Maybe<string> | undefined;
    depth2RegionName?: Maybe<string> | undefined;
    depth3RegionName?: Maybe<string> | undefined;
  };
  categoryName?: string;
  searchKeyword?: string;
};

/**
 * FIXME: allRegionsService package를 jampot도 사용하게 만든다음 해당 스키마와 관련 로직 삭제
 */
const regionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type JpDescriptionData = {
  title: string;
  content: string;
};

/**
 * @see https://docs.google.com/spreadsheets/d/1kaNH9ZTPKCtoxBGlm_qFN-rsulgw_tecaYACr8dgTuU/edit?pli=1&gid=1288991032
 */
export const Jp = {
  AllPageMeta: {
    title: (data: JpTitleData) => {
      const additionalData = [data.categoryName, data.searchKeyword]
        .flatMap((item) =>
          typeof item === "string" && item.length > 0 ? [`${item}の`] : [],
        )
        .join("");

      return `${data.region.name}で${additionalData}中古品・未使用品を買うなら - キャロット（Karrot）`;
    },

    description: (data: JpTitleData) => {
      const regionName = Jp.getRegionName(data.region);

      return match({
        categoryName: data.categoryName,
        searchKeyword: data.searchKeyword,
      })
        .with(
          {
            categoryName: P.string,
            searchKeyword: P.string,
          },
          (result) => {
            return `お得な${result.categoryName}の${result.searchKeyword}が${regionName}で見つかるフリマアプリです。直接の取引だから送料・手数料は一切不要！ご近所さんとだから安心・簡単に取引できる。`;
          },
        )
        .with(
          {
            categoryName: P.string,
            searchKeyword: P.nullish,
          },
          (result) => {
            return `お得な${result.categoryName}の商品が${regionName}で見つかるフリマアプリです。直接の取引だから送料・手数料は一切不要！ご近所さんとだから安心・簡単に取引できる。`;
          },
        )
        .with(
          {
            categoryName: P.nullish,
            searchKeyword: P.string,
          },
          (result) => {
            return `お得な${result.searchKeyword}が${regionName}で見つかるフリマアプリです。直接の取引だから送料・手数料は一切不要！ご近所さんとだから安心・簡単に取引できる。`;
          },
        )
        .with(
          {
            categoryName: P.nullish,
            searchKeyword: P.nullish,
          },
          () => {
            return `お得な商品が${regionName}で見つかるフリマアプリです。直接の取引だから送料・手数料は一切不要！ご近所さんとだから安心・簡単に取引できる。`;
          },
        )
        .exhaustive();
    },
  },

  DetailPageMeta: {
    title: (data: JpDescriptionData) => {
      if (data.title.length <= 0) {
        throw new Error("Jp.DetailPageMeta.title: title is empty");
      }

      return `${data.title} - キャロット（Karrot）`;
    },

    description: (data: JpDescriptionData) => {
      if (data.content.length <= 0) {
        throw new Error("Jp.DetailPageMeta.title: title is empty");
      }

      return truncateMetaDescription(data.content);
    },
  },

  getRegionName: (region: unknown) => {
    const parsedRegion = regionSchema.safeParse(region);
    if (parsedRegion.success) {
      return parsedRegion.data.name;
    }

    if (!isValidRegion(region)) {
      throw new Error("Jp.AllPageMeta.description: region is invalid");
    }

    return [
      region.depth1RegionName,
      region.depth2RegionName,
      region.depth3RegionName,
    ]
      .filter(isString)
      .join("");
  },
};

function isValidRegion(region: unknown): region is {
  depth1RegionName: string;
  depth2RegionName: string;
  depth3RegionName?: Maybe<string> | undefined;
} {
  return (
    isRecordType(region) &&
    typeof region.depth1RegionName === "string" &&
    typeof region.depth2RegionName === "string"
  );
}
